import { DEF, I_ADDRS, Q_ADDRS, PLC_W, PLC_H, PLC_BODY_GRADIENT } from "../../plc-sim/library";
import { store } from "../../plc-sim/store";
import type { DeviceInst } from "../../plc-sim/types";


const on = (d: DeviceInst, k: string) => Boolean(d.state[k]);

function Screws({ n, x, y, gap = 40 }: { n: number; x: number; y: number; gap?: number }) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        <g key={i} transform={`translate(${x + i * gap},${y})`}>
          <rect x={-11} y={-11} width={22} height={22} rx={3} fill="#c7ccd1" stroke="#8e959b" />
          <circle r={7} fill="#9aa1a8" stroke="#6f767c" />
          <path d="M-4 0H4" stroke="#4d5459" strokeWidth={2} />
        </g>
      ))}
    </>
  );
}

/* ------------------------------------------------------------------ PLC */
function TermScrew({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <circle r={9} fill="#cfd4d7" stroke="#8b9195" />
      <circle r={7.5} fill="url(#screwMetal)" />
      <path d="M-5.5 -1.6 L5.5 1.6 L5.5 -1.6 L-5.5 1.6 Z" fill="#7d848a" opacity={0.85} />
    </g>
  );
}

function Led({ x, y, on: lit, color = "#4ade5f" }: { x: number; y: number; on: boolean; color?: string }) {
  return (
    <g transform={`translate(${x},${y})`}>
      {lit && <rect x={-7} y={-6} width={14} height={12} rx={3} fill={color} opacity={0.3} />}
      <rect
        x={-4.5}
        y={-3.5}
        width={9}
        height={7}
        rx={1.5}
        fill={lit ? color : "#3c4247"}
        stroke="#2a2f33"
        strokeWidth={0.6}
      />
    </g>
  );
}

function Plc({ d }: { d: DeviceInst }) {
  void d;
  const run = store.status === "RUN";
  const hasError = store.issues.some((i) => i.level === "error");
  const hasWarning = store.issues.some((i) => i.level === "warning");
  const bit = (a: string) => store.mem.bit(a);

  // Exact centres of the physical LED windows measured from the supplied
  // 840x829 photograph. Every LED keeps its own Y too, avoiding row drift.
  const sx = (px: number) => (px * PLC_W) / 840;
  const sy = (px: number) => (px * PLC_H) / 829;

  const statusLeds = [
    { x: sx(51.566), y: sy(399.940), on: run, color: "#35e45a" },
    { x: sx(72.063), y: sy(400.238), on: hasError, color: "#ff3b30" },
    { x: sx(92.463), y: sy(400.268), on: hasWarning, color: "#ffbf2f" },
  ];

  const diLeds = [
    [458.547, 400.267], [479.264, 400.403], [500.750, 400.357], [522.414, 399.814],
    [543.203, 400.348], [565.584, 400.299], [587.224, 400.362], [608.567, 400.224],
    [663.481, 400.429], [684.644, 400.260], [705.500, 400.250], [726.488, 400.895],
    [747.136, 400.470], [768.066, 400.791],
  ] as const;

  const dqLeds = [
    [457.362, 590.345], [478.943, 589.657], [500.403, 589.468], [521.810, 590.069],
    [543.090, 589.955], [564.603, 590.015], [585.892, 590.031], [607.913, 589.841],
    [663.155, 589.873], [683.717, 589.983],
  ] as const;

  const PhotoLed = ({
    x,
    y,
    lit,
    color = "#35e45a",
  }: {
    x: number;
    y: number;
    lit: boolean;
    color?: string;
  }) => (
    <g pointerEvents="none">
      {lit && (
        <>
          <rect
            x={x - sx(7.5)}
            y={y - sy(6.5)}
            width={sx(15)}
            height={sy(13)}
            rx={sx(2.2)}
            fill={color}
            opacity={0.72}
            filter="url(#photoLedGlow)"
          />
          <rect
            x={x - sx(5.2)}
            y={y - sy(4.4)}
            width={sx(10.4)}
            height={sy(8.8)}
            rx={sx(1.0)}
            fill={color}
            opacity={1}
            stroke="#d7ffdc"
            strokeWidth={sx(0.7)}
          />
        </>
      )}
    </g>
  );

  return (
    <g>
      {/* The exact user-supplied PLC photo, with only its white background
          removed.  The photograph itself is not redrawn or stylized. */}
      <image
        href="/plc-s7-1200.png"
        x={0}
        y={0}
        width={PLC_W}
        height={PLC_H}
        preserveAspectRatio="none"
      />

      {/* Physical PLC status LEDs. */}
      {statusLeds.map((led, i) => (
        <PhotoLed key={`status-${i}`} x={led.x} y={led.y} lit={led.on} color={led.color} />
      ))}

      {/* 14 digital-input indicator windows. */}
      {I_ADDRS.map((a, i) => (
        <PhotoLed key={a} x={sx(diLeds[i]![0])} y={sy(diLeds[i]![1])} lit={bit(a)} />
      ))}

      {/* All ten digital-output indicator windows: Q0.0..Q0.7 + Q1.0..Q1.1. */}
      {Q_ADDRS.map((a, i) => (
        <PhotoLed key={a} x={sx(dqLeds[i]![0])} y={sy(dqLeds[i]![1])} lit={bit(a)} />
      ))}
    </g>
  );
}


/* ------------------------------------------------------------- devices */
function Contactor({ d }: { d: DeviceInst }) {
  const active = on(d, "coil");
  return (
    <g>
      {/* Exact user-supplied Schneider TeSys LC1D25 photo. Only the white
          background was made transparent; the device pixels are otherwise
          untouched. The crop is fitted proportionally inside the original
          222 x 300 logical contactor footprint so existing projects do not move. */}
      <image
        href="/contactor-lc1d25.png"
        x={32.622}
        y={0}
        width={156.757}
        height={300}
        preserveAspectRatio="none"
      />

      {/* Preserve the old contactor's red pilot-lamp behavior, repositioned
          beside the printed "Control" text on the new LC1D25 photo. */}
      <g transform="translate(80,159)" pointerEvents="none">
        {active && <circle r={8.5} fill="#ff2020" opacity={0.28} filter="url(#photoLedGlow)" />}
        <circle
          r={5.4}
          fill={active ? "#ff2020" : "#4a1616"}
          stroke={active ? "#ffd1d1" : "#6b3030"}
          strokeWidth={0.9}
        />
        {active && <circle cx={-1.8} cy={-1.8} r={1.5} fill="#fff" opacity={0.9} />}
      </g>
    </g>
  );
}

function Relay({ d }: { d: DeviceInst }) {
  // لا نُظهر مصباح الريليه إلا أثناء MONITOR/RUN فعلياً.
  const active = store.status === "RUN" && on(d, "coil");
  return (
    <g>
      {/* مساحة الجسم كلها قابلة للسحب، والصورة نفسها لا تسرق ضغطة السحب. */}
      <rect width={150} height={190} fill="transparent" pointerEvents="all" />
      <image
        href="/relay-rxm.png"
        x={0}
        y={0}
        width={150}
        height={190}
        preserveAspectRatio="none"
        pointerEvents="none"
      />

      {/* مصباح الريليه: يضيء عند تغذية الملف 13/14. */}
      {active && (
        <g pointerEvents="none">
          <circle cx={150 * 0.770} cy={190 * 0.598} r={150 * 0.050} fill="#ff2020" opacity={0.20} />
          <circle cx={150 * 0.770} cy={190 * 0.598} r={150 * 0.026} fill="#ff2020" opacity={0.95} />
          <circle cx={150 * 0.760} cy={190 * 0.588} r={1.5} fill="#fff" opacity={0.9} />
        </g>
      )}
    </g>
  );
}

function Timer({ d }: { d: DeviceInst }) {
  // لا نُظهر مصابيح ON / UP إلا أثناء MONITOR/RUN فعلياً.
  const active = store.status === "RUN" && on(d, "coil");
  const preset = Math.max(0, Number(d.props["preset"] ?? 3));
  const elapsed = Math.max(0, Number(d.state["t"] ?? 0));
  const shown = Math.min(elapsed, preset);
  const up = active && elapsed >= preset;

  return (
    <g>
      <rect width={155} height={170} fill="transparent" pointerEvents="all" />
      <image
        href="/timer-pf083a.png"
        x={0}
        y={0}
        width={155}
        height={170}
        preserveAspectRatio="none"
        pointerEvents="none"
      />

      {/* العداد الرقمي: يبدأ من تغذية 2/7 ويعد حتى قيمة الخصائص. */}
      <g pointerEvents="none">
        <rect x={155 * 0.40} y={170 * 0.420} width={155 * 0.20} height={170 * 0.10} rx={4} fill="#15191d" stroke="#2f353a" strokeWidth={1.2} />
        <rect x={155 * 0.40} y={170 * 0.428} width={155 * 0.20} height={170 * 0.088} rx={2.5} fill="#06110d" stroke="#0d4f35" strokeWidth={0.8} />
        <text x={155 * 0.49} y={170 * 0.485} textAnchor="middle" fill="#48ff9b" fontSize={155 * 0.060} fontWeight={900} fontFamily="monospace">
          {shown.toFixed(1)}
        </text>
        <text x={155 * 0.58} y={170 * 0.50} textAnchor="middle" fill="#8affbd" fontSize={155 * 0.035} fontWeight={800}>s</text>
      </g>

      {/* ON يشتغل فور تغذية الملف 2/7. */}
      {active && (
        <g pointerEvents="none">
          <circle cx={155 * 0.373} cy={170 * 0.328} r={155 * 0.030} fill="#ff2020" opacity={0.30} />
          <circle cx={155 * 0.373} cy={170 * 0.328} r={155 * 0.015} fill="#ff2020" opacity={0.70} />
        </g>
      )}

      {/* UP يشتغل بعد اكتمال الزمن. */}
      {up && (
        <g pointerEvents="none">
          <circle cx={155 * 0.618} cy={170 * 0.328} r={155 * 0.030} fill="#ff2020" opacity={0.30} />
          <circle cx={155 * 0.621} cy={170 * 0.328} r={155 * 0.015} fill="#ff2020" opacity={0.70} />
        </g>
      )}
    </g>
  );
}

function Overload({ d }: { d: DeviceInst }) {
  const tripped = on(d, "tripped");
  const monitor = store.status === "RUN";
  const setTrip = (value: boolean) => {
    d.state["tripped"] = value;
    store.touch();
    if (store.status === "RUN") store.scan();
  };

  return (
    <g>
      {/* نسخة الأوفرلود نفسها الموجودة في محاكي الدوائر الكهربائية:
          Schneider TeSys LRD06، بنفس قياس العرض 180×250. */}
      <rect width={180} height={250} fill="transparent" pointerEvents="all" />
      <image
        href="/overload-lrd06.png"
        x={0}
        y={0}
        width={180}
        height={250}
        preserveAspectRatio="none"
        pointerEvents="none"
      />

      {/* مصباح Trip الأحمر بنفس موضع محاكي الدوائر الكهربائية. */}
      {tripped && (
        <g pointerEvents="none">
          <circle cx={119} cy={116} r={10.5} fill="#ff1f1f" opacity={0.22} filter="url(#photoLedGlow)" />
          <circle cx={119} cy={116} r={5.8} fill="#ff1f1f" stroke="#8b0000" strokeWidth={1.1} />
          <circle cx={117.4} cy={114.4} r={1.5} fill="#fff" opacity={0.82} />
        </g>
      )}

      {/* أزرار STOP و RESET الحقيقية تعمل فقط أثناء MONITOR/RUN،
          مثل محاكي الدوائر الكهربائية تماماً. */}
      {monitor && (
        <g>
          <circle
            cx={144.8}
            cy={117.2}
            r={15}
            fill="transparent"
            pointerEvents="all"
            style={{ cursor: "pointer" }}
            onPointerDown={(e) => {
              e.stopPropagation();
              setTrip(true);
            }}
          />
          <circle
            cx={145.4}
            cy={70.1}
            r={15}
            fill="transparent"
            pointerEvents="all"
            style={{ cursor: "pointer" }}
            onPointerDown={(e) => {
              e.stopPropagation();
              setTrip(false);
            }}
          />
        </g>
      )}
    </g>
  );
}

function McbPhoto({ d, poles }: { d: DeviceInst; poles: 1 | 2 | 3 }) {
  const closed = d.state["on"] !== false;
  const cfg =
    poles === 1
      ? { href: "/mcb-1p.png", w: 56, h: 260, leverX: 9, leverW: 38, coverY: 135, coverH: 47, leverY: 137 }
      : poles === 2
        ? { href: "/mcb-2p.png", w: 108, h: 240, leverX: 8, leverW: 92, coverY: 112, coverH: 49, leverY: 115 }
        : { href: "/mcb-3p.png", w: 154, h: 260, leverX: 7, leverW: 140, coverY: 137, coverH: 45, leverY: 140 };

  return (
    <g>
      <image href={cfg.href} x={0} y={0} width={cfg.w} height={cfg.h} preserveAspectRatio="none" />
      {closed && (
        <g pointerEvents="none">
          <rect
            x={cfg.leverX}
            y={cfg.coverY}
            width={cfg.leverW}
            height={cfg.coverH}
            rx={2}
            fill="#e3e5e5"
            opacity={0.93}
          />
          <rect
            x={cfg.leverX + 2}
            y={cfg.leverY}
            width={cfg.leverW - 4}
            height={Math.max(11, cfg.coverH * 0.32)}
            rx={2.5}
            fill="#17191a"
            stroke="#050606"
            strokeWidth={0.8}
          />
        </g>
      )}
    </g>
  );
}

function Psu({ d }: { d: DeviceInst }) {
  const live = d.state["off"] !== true;
  return (
    <g>
      <rect width={170} height={130} rx={5} fill="#c9ced2" stroke="#9aa1a6" />
      <rect x={10} y={10} width={90} height={90} rx={3} fill="#b6bcc1" />
      {Array.from({ length: 8 }, (_, i) => (
        <line key={i} x1={16 + i * 10} y1={16} x2={16 + i * 10} y2={94} stroke="#9aa1a6" />
      ))}
      <rect x={108} y={16} width={54} height={30} rx={3} fill="#e8ebed" />
      <text x={135} y={36} fontSize={9} textAnchor="middle" fill="#33383c">
        24VDC
      </text>
      <circle cx={118} cy={62} r={5} fill={live ? "#3fbf4f" : "#7d858b"} />
      <text x={128} y={66} fontSize={8} fill="#33383c">
        DC OK
      </text>
    </g>
  );
}

function Src({ d, label, w }: { d: DeviceInst; label: string; w: number }) {
  const live = d.state["off"] !== true;
  return (
    <g>
      <rect width={w} height={110} rx={6} fill="#33383c" stroke="#1d2124" />
      <rect x={10} y={22} width={w - 20} height={48} rx={4} fill="#464d52" />
      <text x={w / 2} y={52} fontSize={16} textAnchor="middle" fill="#e9eef2" fontFamily="monospace">
        {label}
      </text>
      <circle cx={16} cy={16} r={5} fill={live ? "#3fbf4f" : "#6d757b"} />
    </g>
  );
}

function PushButton({ d, color, ring }: { d: DeviceInst; color: string; ring: string }) {
  const pressed = on(d, "pressed");
  return (
    <g>
      <rect width={110} height={110} rx={8} fill="#26292c" stroke="#141618" />
      <circle cx={55} cy={48} r={32} fill="#1a1d20" />
      <circle cx={55} cy={48} r={26} fill={ring} />
      <circle
        cx={55}
        cy={pressed ? 50 : 46}
        r={pressed ? 20 : 23}
        fill={color}
        stroke="#00000055"
      />
      <text x={55} y={92} fontSize={9} fill="#cfd4d8" textAnchor="middle">
        {pressed ? "PRESSED" : "PUSH"}
      </text>
    </g>
  );
}

function EStop({ d }: { d: DeviceInst }) {
  const latched = on(d, "latched");
  return (
    <g>
      <circle cx={60} cy={54} r={48} fill="#e8c400" stroke="#b39a00" />
      <text x={60} y={16} fontSize={8} textAnchor="middle" fill="#5a4d00">
        EMERGENCY
      </text>
      <circle cx={60} cy={56} r={latched ? 28 : 31} fill="#cc1f1a" stroke="#7c110e" />
      <text x={60} y={100} fontSize={8} textAnchor="middle" fill="#5a4d00">
        STOP
      </text>
      {latched && <circle cx={60} cy={56} r={10} fill="#8c1512" />}
    </g>
  );
}

function Selector({ d }: { d: DeviceInst }) {
  const man = on(d, "man");
  return (
    <g>
      <rect width={110} height={120} rx={8} fill="#26292c" stroke="#141618" />
      <circle cx={55} cy={56} r={34} fill="#e6e9eb" />
      <text x={22} y={22} fontSize={8} fill="#cfd4d8">
        AUTO
      </text>
      <text x={72} y={22} fontSize={8} fill="#cfd4d8">
        MAN
      </text>
      <g transform={`rotate(${man ? 35 : -35},55,56)`}>
        <rect x={51} y={26} width={8} height={34} rx={3} fill="#1d2124" />
      </g>
      <circle cx={55} cy={56} r={8} fill="#1d2124" />
    </g>
  );
}

function Lamp({ d }: { d: DeviceInst }) {
  const lit = on(d, "lit");
  const col = d.props["color"] ?? "green";
  const base =
    col === "red" ? "#7a1512" : col === "amber" ? "#7a5300" : col === "blue" ? "#123f7a" : "#136321";
  const bright =
    col === "red" ? "#ff4a42" : col === "amber" ? "#ffbe2e" : col === "blue" ? "#4aa7ff" : "#48e05f";
  return (
    <g>
      <rect width={90} height={110} rx={8} fill="#26292c" stroke="#141618" />
      <circle cx={45} cy={46} r={30} fill="#1a1d20" />
      <circle cx={45} cy={46} r={24} fill={lit ? bright : base} />
      {lit && <circle cx={45} cy={46} r={34} fill={bright} opacity={0.25} />}
      <text x={45} y={92} fontSize={9} fill="#cfd4d8" textAnchor="middle">
        {lit ? "ON" : "OFF"}
      </text>
    </g>
  );
}

function Buzzer({ d }: { d: DeviceInst }) {
  const lit = on(d, "lit");
  return (
    <g>
      <rect width={100} height={110} rx={8} fill="#1e2124" stroke="#111315" />
      <circle cx={50} cy={48} r={30} fill="#2a2e32" />
      {[8, 14, 20, 26].map((r) => (
        <circle key={r} cx={50} cy={48} r={r} fill="none" stroke="#3d4348" />
      ))}
      {lit && <circle cx={50} cy={48} r={34} fill="#ffb020" opacity={0.28} />}
      <text x={50} y={94} fontSize={9} fill="#cfd4d8" textAnchor="middle">
        BUZZER
      </text>
    </g>
  );
}

function Motor({ d }: { d: DeviceInst }) {
  const run = on(d, "run");
  const angle = Number(d.state["angle"] ?? 0);
  const direction = String(d.state["direction"] ?? "stopped");
  const dirLabel = direction === "forward" ? "→ FORWARD" : direction === "reverse" ? "← REVERSE" : "—";
  return (
    <g>
      <rect x={20} y={30} width={130} height={100} rx={22} fill="#1f4e9c" stroke="#143a75" />
      {Array.from({ length: 9 }, (_, i) => (
        <line key={i} x1={30 + i * 14} y1={32} x2={30 + i * 14} y2={128} stroke="#17417f" />
      ))}
      <rect x={150} y={62} width={26} height={36} rx={4} fill="#2b6ac9" />
      <rect x={176} y={74} width={22} height={12} rx={3} fill="#b8bfc4" />
      <circle cx={85} cy={80} r={26} fill="#17417f" />
      <g transform={`rotate(${angle},85,80)`}>
        <rect x={82} y={56} width={6} height={48} rx={3} fill={run ? "#48e05f" : "#7f8b96"} />
      </g>
      <text x={85} y={143} fontSize={10} fontWeight={700} textAnchor="middle" fill={run ? "#31d653" : "#8b98a3"}>
        {run ? dirLabel : "STOPPED"}
      </text>
      <text x={85} y={156} fontSize={8.5} textAnchor="middle" fill="#cfd8e0">
        {run ? (direction === "forward" ? "مع عقارب الساعة" : "عكس عقارب الساعة") : "بانتظار 3 فازات"}
      </text>
    </g>
  );
}

function MotorStarDelta({ d }: { d: DeviceInst }) {
  const run = on(d, "run");
  const angle = Number(d.state["angle"] ?? 0);
  const direction = String(d.state["direction"] ?? "stopped");
  const connection = String(d.state["connection"] ?? "incomplete");
  const dirLabel = direction === "forward" ? "→ FORWARD" : direction === "reverse" ? "← REVERSE" : "—";
  const connectionLabel = connection === "star" ? "STAR (Y)" : connection === "delta" ? "DELTA (Δ)" : "غير مكتمل";

  const terminal = (x: number, y: number, label: string) => (
    <g key={label} transform={`translate(${x},${y})`} pointerEvents="none">
      <circle r={9} fill="#d4d8db" stroke="#7d858b" strokeWidth={1} />
      <circle r={6.5} fill="#aeb5ba" stroke="#737b81" strokeWidth={0.8} />
      <path d="M-4 0H4" stroke="#555d63" strokeWidth={1.4} />
      <text x={0} y={-11.5} fontSize={8.5} fontWeight={700} textAnchor="middle" fill="#263238">
        {label}
      </text>
    </g>
  );

  return (
    <g>
      {/* Six-terminal motor board: U1 V1 W1 / U2 V2 W2. */}
      <rect x={38} y={7} width={114} height={62} rx={6} fill="#eceff1" stroke="#8c969d" />
      <rect x={44} y={13} width={102} height={50} rx={4} fill="#dfe4e7" stroke="#b0b8bd" />
      {terminal(58, 24, "U1")}
      {terminal(95, 24, "V1")}
      {terminal(132, 24, "W1")}
      {terminal(58, 54, "U2")}
      {terminal(95, 54, "V2")}
      {terminal(132, 54, "W2")}

      {/* Motor body. */}
      <rect x={20} y={78} width={150} height={106} rx={24} fill="#1f4e9c" stroke="#143a75" />
      {Array.from({ length: 10 }, (_, i) => (
        <line key={i} x1={31 + i * 14} y1={80} x2={31 + i * 14} y2={182} stroke="#17417f" />
      ))}
      <rect x={170} y={111} width={28} height={38} rx={4} fill="#2b6ac9" />
      <rect x={198} y={124} width={30} height={12} rx={3} fill="#b8bfc4" />
      <circle cx={95} cy={130} r={28} fill="#17417f" />
      <g transform={`rotate(${angle},95,130)`}>
        <rect x={92} y={104} width={6} height={52} rx={3} fill={run ? "#48e05f" : "#7f8b96"} />
      </g>

      <rect x={40} y={187} width={110} height={18} rx={6} fill="#152f5c" opacity={0.96} />
      <text x={95} y={198.5} fontSize={9.5} fontWeight={700} textAnchor="middle" fill={run ? "#52ef6b" : "#c9d2d8"}>
        {run ? dirLabel : `STOPPED • ${connectionLabel}`}
      </text>
      {run && (
        <text x={183} y={198.5} fontSize={8.5} textAnchor="middle" fill="#41525c">
          {connectionLabel}
        </text>
      )}
    </g>
  );
}

function LimitSwitch({ d }: { d: DeviceInst }) {
  const act = on(d, "on");
  return (
    <g>
      <rect x={10} y={30} width={90} height={90} rx={6} fill="#1f4e9c" stroke="#143a75" />
      <g transform={`rotate(${act ? 25 : -15},30,28)`}>
        <rect x={28} y={4} width={48} height={7} rx={3} fill="#b9c0c6" />
        <circle cx={78} cy={8} r={7} fill="#8d959b" />
      </g>
      <rect x={20} y={60} width={70} height={34} rx={4} fill="#e9ecee" />
      <text x={55} y={82} fontSize={9} textAnchor="middle" fill="#333">
        LIMIT
      </text>
      <circle cx={92} cy={40} r={5} fill={act ? "#3fbf4f" : "#7f8b96"} />
    </g>
  );
}

function Prox({ d }: { d: DeviceInst }) {
  const act = on(d, "on");
  return (
    <g>
      <rect x={6} y={22} width={40} height={34} rx={6} fill="#b9c0c6" stroke="#8d959b" />
      <rect x={46} y={26} width={54} height={26} rx={4} fill="#8d959b" />
      <circle cx={112} cy={39} r={16} fill="#1f4e9c" stroke="#143a75" />
      <circle cx={112} cy={39} r={7} fill={act ? "#48e05f" : "#2f6fd0"} />
      <text x={60} y={68} fontSize={9} fill="#41525c">
        PROX {act ? "ON" : "OFF"}
      </text>
    </g>
  );
}

function Hmi({ d }: { d: DeviceInst }) {
  const run = Boolean(d.state["run"]);
  return (
    <g>
      <rect width={220} height={160} rx={10} fill="#26292c" stroke="#141618" />
      <rect x={12} y={12} width={196} height={116} rx={5} fill="#f4f6f8" />
      <rect x={12} y={12} width={196} height={22} rx={5} fill="#e2e7eb" />
      <text x={200} y={28} fontSize={10} textAnchor="end" fill="#41525c">
        نظام تحكم
      </text>
      <rect x={120} y={44} width={80} height={22} rx={4} fill={run ? "#2f9e44" : "#c8ced3"} />
      <text x={160} y={59} fontSize={10} textAnchor="middle" fill="#fff">
        {run ? "تشغيل" : "متوقف"}
      </text>
      <text x={110} y={59} fontSize={10} textAnchor="end" fill="#41525c">
        حالة النظام
      </text>
      {[
        ["#2f9e44", "تشغيل"],
        ["#e0322c", "إيقاف"],
        ["#2f6fd0", "إعدادات"],
      ].map(([c, l], i) => (
        <g key={l} transform={`translate(${22 + i * 62},76)`}>
          <rect width={54} height={44} rx={5} fill="#eef2f5" stroke="#cfd6db" />
          <circle cx={27} cy={16} r={9} fill={c as string} />
          <text x={27} y={38} fontSize={9} textAnchor="middle" fill="#41525c">
            {l}
          </text>
        </g>
      ))}
    </g>
  );
}

function TerminalStrip() {
  return (
    <g>
      <rect y={20} width={240} height={40} rx={3} fill="#b9c0c6" stroke="#8d959b" />
      {Array.from({ length: 8 }, (_, i) => (
        <rect
          key={i}
          x={8 + i * 28}
          y={22}
          width={24}
          height={36}
          rx={2}
          fill={["#2f6fd0", "#e0e4e8", "#e8c400"][i % 3]}
          stroke="#8d959b"
        />
      ))}
    </g>
  );
}

export function DeviceBody({ d }: { d: DeviceInst }) {
  switch (d.type) {
    case "plc":
      return <Plc d={d} />;
    case "contactor":
      return <Contactor d={d} />;
    case "relay":
      return <Relay d={d} />;
    case "timer":
      return <Timer d={d} />;
    case "overload":
      return <Overload d={d} />;
    case "mcb1":
      return <McbPhoto d={d} poles={1} />;
    case "mcb":
      return <McbPhoto d={d} poles={2} />;
    case "mcb3":
      return <McbPhoto d={d} poles={3} />;
    case "psu24":
      return <Psu d={d} />;
    case "src220":
      return <Src d={d} label="220VAC" w={150} />;
    case "src380":
      return <Src d={d} label="380VAC" w={190} />;
    case "pb_start":
      return <PushButton d={d} color="#2f9e44" ring="#1a1d20" />;
    case "pb_stop":
      return <PushButton d={d} color="#cc1f1a" ring="#1a1d20" />;
    case "estop":
      return <EStop d={d} />;
    case "selector":
      return <Selector d={d} />;
    case "lamp":
      return <Lamp d={d} />;
    case "buzzer":
      return <Buzzer d={d} />;
    case "motor":
      return <Motor d={d} />;
    case "motor_star_delta":
      return <MotorStarDelta d={d} />;
    case "limit":
      return <LimitSwitch d={d} />;
    case "prox":
      return <Prox d={d} />;
    case "hmi":
      return <Hmi d={d} />;
    case "terminals":
      return <TerminalStrip />;
    default:
      return <rect width={100} height={80} rx={6} fill="#8d959b" />;
  }
}

export function SvgDefs() {
  return (
    <defs>
      <linearGradient id="plcBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={PLC_BODY_GRADIENT[0]} />
        <stop offset="45%" stopColor={PLC_BODY_GRADIENT[1]} />
        <stop offset="100%" stopColor={PLC_BODY_GRADIENT[2]} />
      </linearGradient>
      <linearGradient id="screwMetal" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#eef1f3" />
        <stop offset="50%" stopColor="#b8bec3" />
        <stop offset="100%" stopColor="#8d9499" />
      </linearGradient>
      <filter id="photoLedGlow" x="-200%" y="-200%" width="500%" height="500%">
        <feGaussianBlur stdDeviation="2.2" />
      </filter>

    </defs>
  );
}

export { Screws };
